/* eslint-disable react/no-array-index-key */
import { useLayoutEffect, useEffect, useState, FC } from 'react';
import { Box, Flex, Heading } from '@rebass/emotion';
import { FormattedMessage } from 'react-intl';
import { match as MatchInterface } from 'react-router-dom';
import { ProductsResult } from '../ProductsProvider';
import ProductTile from '../ProductTile';
import ErrorContent from '../ErrorContent';
import styles from './ProductList.styles';
import Button from '../../Button';
import messages from './ProductList.messages';
import canUseDOM from '../../../helpers/canUseDOM';
import ProductPlaceholders from '../ProductPlaceholders';

export const PRODUCT_LIST_GRID = [1, 1 / 2, 1 / 3, 1 / 4];

export interface ProductListProps {
  productsResult: DeepOmit<ProductsResult, '__typename'>;
  grid?: number[]; // define the colums of the grid.
  loading: boolean;
  error: boolean;
  page: number;
  match: MatchInterface;
}

const ProductList: FC<ProductListProps> = ({
  productsResult,
  loading,
  error,
  grid,
  match,
}) => {
  const useIsomorphicLayoutEffect = canUseDOM() ? useLayoutEffect : useEffect;
  const [triggerPageAnimation, setTriggerPageAnimation] = useState(false);
  const { url } = match;

  // Animates page every time URL changes
  useIsomorphicLayoutEffect(() => {
    setTriggerPageAnimation(false);
    setTimeout(() => setTriggerPageAnimation(true));
  }, [url]);

  if (error) {
    return (
      <ErrorContent products={productsResult} data-testid="error-message" />
    );
  }

  const { payLoad: products = [], fetchMore, pageInfo } = productsResult;
  const { currentPage, hasNextPage } = pageInfo ?? {};

  if (!loading && products.length === 0) {
    return (
      <Heading pb={4} mt={4} textAlign="center" data-testid="no-products">
        <FormattedMessage {...messages.noProducts} />
      </Heading>
    );
  }

  const loadMore = (): void => {
    fetchMore({
      variables: {
        page: hasNextPage ? (currentPage ?? 1) + 1 : currentPage ?? 1,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const result = fetchMoreResult;

        // merge previous items with new ones
        result.getProducts.result.items = [
          ...previousResult.getProducts.result.items,
          ...fetchMoreResult.getProducts.result.items,
        ];
        return result;
      },
    });
  };

  return (
    <Box data-testid="product-finder-product-list-wrapper">
      <Flex css={styles.mainWrapper(triggerPageAnimation)}>
        {products.map((product, i) => (
          <Box
            css={styles.boxStyle()}
            key={i}
            px={[0, 2]}
            py={3}
            width={grid}
            className="product-box"
          >
            <ProductTile product={product} />
          </Box>
        ))}
      </Flex>
      {loading ? (
        <Box
          css={styles.loadMoreWrapper}
          data-testid="product-finder-product-list-preloader"
        >
          <ProductPlaceholders count={8} />
        </Box>
      ) : (
        hasNextPage && (
          <Flex css={styles.loadMoreWrapper} py={4}>
            {/* TODO: use new button > app/shop/components/Button.tsx */}
            {/* @ts-expect-error derived types unusable, not worth ts conversion given above todo */}
            <Button onClick={loadMore} data-testid="load-more">
              <FormattedMessage {...messages.loadMore} />
            </Button>
          </Flex>
        )
      )}
    </Box>
  );
};

ProductList.defaultProps = {
  grid: PRODUCT_LIST_GRID,
};

export default ProductList;
